import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';

import KDSCombobox from '@decisiv/ui-components/lib/components/Combobox';
import { ComboboxProps } from '@decisiv/ui-components/lib/components/Combobox/types';
import { Items } from '@decisiv/ui-components/lib/components/DropdownList';
import { ItemOption } from '@decisiv/ui-components/lib/atoms/OptionsList/types';

type Option = { id: string; label: string; value?: string };

interface FormComboboxProps
  extends Omit<
    ComboboxProps,
    'value' | 'onChange' | 'options' | 'defaultValue'
  > {
  name: string;
  options: Option[];
  multiple?: boolean;
  defaultValue?: string | string[];
  disabled?: boolean;
  onChange?: (value: string) => void;
}

const Combobox = ({
  name,
  defaultValue,
  options,
  disabled,
  multiple = false,
  variant = 'sync',
  onChange: externalOnChange,
  onChangeInputValue: externalOnChangeInputValue,
  ...props
}: FormComboboxProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [typedValue, setTypedValue] = useState<string | null>(null);

  const handleInputValueChange = (event?: React.ChangeEvent) => {
    const target = event?.target as HTMLInputElement | undefined;
    setTypedValue(target?.value ?? '');
    externalOnChangeInputValue?.(event);
  };

  const handleMultiChange = (
    currentValue: string[],
    id: string | null,
    removed: boolean,
    onChange: (value: string[]) => void,
  ) => {
    /* istanbul ignore next -- @preserve: KDS types allow null on clear */
    if (id === null) return;
    const value = removed
      ? currentValue.filter((currId: string) => currId !== id)
      : uniq([...currentValue, id]);
    onChange(value);
  };

  const multiValue = (currentValue: string[]): Items =>
    currentValue
      .map((currVal) => options.find((option) => option.value === currVal))
      .filter((option): option is Option => option !== undefined);

  const handleSingleChange = (
    id: string | null,
    onChange: (value: string) => void,
  ) => {
    const selectedOption = options.find((option) => option.id === id);
    const newValue = (selectedOption as ItemOption | undefined)?.value ?? '';
    externalOnChange?.(newValue);
    onChange(newValue);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue ?? (multiple ? [] : '')}
      render={({ field: { onChange, value } }) => {
        const selectedOption = options.find((option) => option.value === value);
        const selectedLabel = selectedOption?.label ?? '';
        // Show the user's in-progress text while typing; otherwise show the selected label.
        const displayValue = typedValue ?? selectedLabel;
        const searchTerm = typedValue ?? '';
        const isSearching =
          variant === 'sync' &&
          searchTerm !== '' &&
          searchTerm !== selectedLabel;
        const visibleOptions = isSearching
          ? options.filter((option) =>
              option.label.toLowerCase().includes(searchTerm.toLowerCase()),
            )
          : options;

        return multiple ? (
          <KDSCombobox
            value={multiValue(value)}
            options={options}
            multiple
            variant={variant}
            disabled={disabled || isEmpty(options)}
            onChangeValue={(id, { removed }) =>
              handleMultiChange(value, id, removed, onChange)
            }
            warningMessage={errors[name]?.message?.toString()}
            {...props}
          />
        ) : (
          <KDSCombobox
            value={value}
            inputValue={displayValue}
            options={visibleOptions}
            variant={variant}
            disabled={disabled}
            onChangeValue={(id) => {
              handleSingleChange(id, onChange);
              setTypedValue(null);
            }}
            onChangeInputValue={handleInputValueChange}
            warningMessage={errors[name]?.message?.toString()}
            {...props}
          />
        );
      }}
    />
  );
};

export default Combobox;
